import { MoveHorizontal } from "lucide-react";
import {
  type KeyboardEvent,
  type PointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { mediaUrl } from "lib/media";

const FRAME_COUNT = 30;
const DRAG_PIXELS_PER_FRAME = 10;
const FRAME_ROOT = mediaUrl("website_files/360chevy/pngs");

const chevyFrames = Array.from({ length: FRAME_COUNT }, (_, index) => {
  const frameNumber = String(index + 1).padStart(3, "0");
  return `${FRAME_ROOT}/my26-silverado-3lt-gjv-ext_${frameNumber}.png`;
});

const normalizeFrame = (frame: number) =>
  ((frame % FRAME_COUNT) + FRAME_COUNT) % FRAME_COUNT;

interface DragState {
  pointerId: number;
  startFrame: number;
  startX: number;
}

export const Car360Viewer = () => {
  const [frameIndex, setFrameIndex] = useState(0);
  const [loadedCount, setLoadedCount] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const frameRef = useRef(0);
  const dragRef = useRef<DragState | null>(null);

  const showFrame = useCallback((nextFrame: number) => {
    const normalizedFrame = normalizeFrame(nextFrame);
    frameRef.current = normalizedFrame;
    setFrameIndex(normalizedFrame);
  }, []);

  useEffect(() => {
    let isActive = true;
    let completedImages = 0;
    const preloadedImages = chevyFrames.map((src) => {
      const image = new Image();
      const markComplete = () => {
        completedImages += 1;
        if (isActive) {
          setLoadedCount(completedImages);
        }
      };

      image.onload = markComplete;
      image.onerror = markComplete;
      image.src = src;
      return image;
    });

    return () => {
      isActive = false;
      preloadedImages.forEach((image) => {
        image.onload = null;
        image.onerror = null;
      });
    };
  }, []);

  useEffect(() => {
    if (!isReady || hasInteracted) {
      return;
    }

    let completedFrames = 0;
    const autoplayTimer = window.setInterval(() => {
      showFrame(frameRef.current + 1);
      completedFrames += 1;
      if (completedFrames >= FRAME_COUNT - 1) {
        window.clearInterval(autoplayTimer);
      }
    }, 95);

    return () => window.clearInterval(autoplayTimer);
  }, [hasInteracted, isReady, showFrame]);

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) {
      return;
    }

    event.preventDefault();
    event.currentTarget.focus({ preventScroll: true });
    event.currentTarget.setPointerCapture?.(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      startFrame: frameRef.current,
      startX: event.clientX,
    };
    setHasInteracted(true);
    setIsDragging(true);
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const dragState = dragRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) {
      return;
    }

    event.preventDefault();
    const draggedFrames = Math.trunc(
      (event.clientX - dragState.startX) / DRAG_PIXELS_PER_FRAME,
    );
    showFrame(dragState.startFrame - draggedFrames);
  };

  const finishPointerInteraction = (event: PointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.pointerId !== event.pointerId) {
      return;
    }

    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    }
    dragRef.current = null;
    setIsDragging(false);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") {
      return;
    }

    event.preventDefault();
    setHasInteracted(true);
    showFrame(frameRef.current + (event.key === "ArrowRight" ? 1 : -1));
  };

  return (
    <div
      className={`chevy-360-shell relative aspect-[16/11] touch-none overflow-hidden border border-white/10 bg-[#0b0f16] outline-none focus-visible:border-ember ${
        isDragging ? "cursor-grabbing" : "cursor-grab"
      }`}
      role="slider"
      tabIndex={0}
      aria-label="Interactive 360 degree view of a Chevrolet Silverado"
      aria-valuemin={1}
      aria-valuemax={FRAME_COUNT}
      aria-valuenow={frameIndex + 1}
      aria-valuetext={`Frame ${frameIndex + 1} of ${FRAME_COUNT}`}
      aria-orientation="horizontal"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={finishPointerInteraction}
      onPointerCancel={finishPointerInteraction}
      onKeyDown={handleKeyDown}
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_48%,rgba(46,111,184,0.22),transparent_42%)]" />
      <div className="pointer-events-none absolute inset-x-[19%] bottom-[21%] h-[7%] rounded-full bg-black/70 blur-2xl" />

      <div className="pointer-events-none absolute inset-0 grid place-items-center">
        <img
          src={chevyFrames[frameIndex]}
          alt="Blue Chevrolet Silverado ZR2"
          className="h-[76%] w-[76%] select-none object-contain"
          draggable={false}
          onLoad={() => setIsReady(true)}
        />
      </div>

      <div className="pointer-events-none absolute left-5 top-5 z-20 flex items-center gap-3 text-[10px] font-bold uppercase tracking-[0.2em] text-white sm:left-7 sm:top-7 sm:text-xs">
        <span className="h-2 w-2 rounded-full bg-ember shadow-[0_0_18px_rgba(255,92,54,0.9)]" />
        Interactive 360°
      </div>

      <div className="pointer-events-none absolute bottom-5 left-5 z-20 text-[10px] font-bold uppercase tracking-[0.16em] text-white/40 sm:bottom-7 sm:left-7">
        {loadedCount < FRAME_COUNT
          ? `Loading ${loadedCount} / ${FRAME_COUNT}`
          : `Frame ${String(frameIndex + 1).padStart(2, "0")} / ${FRAME_COUNT}`}
      </div>

      <div className="pointer-events-none absolute bottom-5 right-5 z-20 hidden items-center gap-2 text-[10px] font-bold uppercase tracking-[0.16em] text-white/50 sm:bottom-7 sm:right-7 sm:flex">
        <MoveHorizontal className="h-4 w-4 text-ember" />
        {isDragging ? "Rotating" : "Drag to rotate"}
      </div>
    </div>
  );
};
